import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faTrophy,
  faDownload,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import './GeoGameOver.css';
import { fetchTopThreeGeoFinalistStream } from '../../apiservices/axiosservice/gameservice';
import { mockTopPlayerSummary, mockGeoResult } from '../../data/mockdata';
import { randomColor } from '../../utils/shareUtils';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { fetchCurrentGeoRoundResult } from '../../apiservices/axiosservice/subscriberservice';
import Confetti from '../animations/Confetti';

interface RoundBreakdown {
  round: number;
  cityName: string;
  distance: string;
  maxScore: number;
  topPlayers: { name: string; initials: string; color: string }[];
}

interface FinalPlayer {
  rank: number;
  playerName: string;
  playerIcon: string;
  iconColor: string;
  score: number;
  scoreDelta: number;
}

interface TargetInfo {
  cityName: string;
  country: string;
  region: string;
  lat: number;
  lng: number;
  population: string;
  type: string;
}

interface PlayerPin {
  playerName: string;
  iconColor: string;
  lat: number;
  lng: number;
}

interface GeoGameOverData {
  gameTitle: string;
  mapScope: string;
  date: string;
  totalPlayers: number;
  avgAccuracy: number;
  totalDuration: string;
  totalRounds: number;
  target: TargetInfo;
  keyClue: string;
  players: FinalPlayer[];
  rounds: RoundBreakdown[];
  playerPins: PlayerPin[];
}

const mock: GeoGameOverData = {
  gameTitle: 'World Capitals Quiz',
  mapScope: 'World Map',
  date: '25 Mar 2026',
  totalPlayers: 7,
  avgAccuracy: 62,
  totalDuration: '7 min',
  totalRounds: 5,
  target: {
    cityName: 'Kyiv',
    country: 'Ukraine',
    region: 'Eastern Europe',
    lat: 50.4501,
    lng: 30.5234,
    population: '2.9M',
    type: 'Capital city',
  },
  keyClue:
    "The road sign shows 'KYIV 84 km' in Cyrillic-influenced Latin — Ukraine switched to Latin transliteration for highway signs in 2019",
  players: [
    {
      rank: 1,
      playerName: 'FoxRunner',
      playerIcon: '🦊',
      iconColor: '#FF6B6B',
      score: 10280,
      scoreDelta: 1920,
    },
    {
      rank: 2,
      playerName: 'WhaleRider',
      playerIcon: '🐋',
      iconColor: '#4ECDC4',
      score: 9240,
      scoreDelta: 1920,
    },
    {
      rank: 3,
      playerName: 'RocketGal',
      playerIcon: '🚀',
      iconColor: '#AB47BC',
      score: 8840,
      scoreDelta: 1920,
    },
    {
      rank: 4,
      playerName: 'GreenLeaf42',
      playerIcon: '🌿',
      iconColor: '#66BB6A',
      score: 7960,
      scoreDelta: 1920,
    },
    {
      rank: 5,
      playerName: 'LionKing',
      playerIcon: '🦁',
      iconColor: '#F9A825',
      score: 6800,
      scoreDelta: 1920,
    },
    {
      rank: 6,
      playerName: 'MagicHat99',
      playerIcon: '🎩',
      iconColor: '#AB47BC',
      score: 5940,
      scoreDelta: 1920,
    },
    {
      rank: 7,
      playerName: 'PetalDrop',
      playerIcon: '🌸',
      iconColor: '#FF6B6B',
      score: 4200,
      scoreDelta: 1920,
    },
  ],
  rounds: [
    {
      round: 1,
      cityName: 'Paris',
      distance: 'only 86 km off',
      maxScore: 4340,
      topPlayers: [
        { name: 'WhaleRider', initials: 'SK', color: '#4ECDC4' },
        { name: 'WhaleRider', initials: 'AT', color: '#F9A825' },
        { name: 'WhaleRider', initials: 'JA', color: '#66BB6A' },
      ],
    },
    {
      round: 2,
      cityName: 'Kyiv',
      distance: '847 km',
      maxScore: 3620,
      topPlayers: [
        { name: 'WhaleRider', initials: 'SK', color: '#4ECDC4' },
        { name: 'WhaleRider', initials: 'AT', color: '#F9A825' },
        { name: 'WhaleRider', initials: 'JA', color: '#66BB6A' },
      ],
    },
    {
      round: 3,
      cityName: 'El Paso',
      distance: '1,240 km',
      maxScore: 3500,
      topPlayers: [
        { name: 'WhaleRider', initials: 'SK', color: '#4ECDC4' },
        { name: 'WhaleRider', initials: 'AT', color: '#F9A825' },
        { name: 'WhaleRider', initials: 'JA', color: '#66BB6A' },
      ],
    },
    {
      round: 4,
      cityName: 'R2 Kyiv',
      distance: '980 km',
      maxScore: 3390,
      topPlayers: [
        { name: 'WhaleRider', initials: 'SK', color: '#4ECDC4' },
        { name: 'WhaleRider', initials: 'AT', color: '#F9A825' },
        { name: 'WhaleRider', initials: 'JA', color: '#66BB6A' },
      ],
    },
    {
      round: 5,
      cityName: 'R3 Tokyo',
      distance: '620 km',
      maxScore: 3720,
      topPlayers: [
        { name: 'WhaleRider', initials: 'SK', color: '#4ECDC4' },
        { name: 'WhaleRider', initials: 'AT', color: '#F9A825' },
        { name: 'WhaleRider', initials: 'JA', color: '#66BB6A' },
      ],
    },
  ],
  playerPins: [
    { playerName: 'FoxRunner', iconColor: '#FF6B6B', lat: 50.35, lng: 30.4 },
    { playerName: 'WhaleRider', iconColor: '#4ECDC4', lat: 50.2, lng: 30.15 },
    { playerName: 'RocketGal', iconColor: '#AB47BC', lat: 50.3, lng: 30.55 },
    { playerName: 'GreenLeaf42', iconColor: '#66BB6A', lat: 50.1, lng: 30.8 },
    { playerName: 'LionKing', iconColor: '#F9A825', lat: 49.95, lng: 30.2 },
  ],
};

const podiumColors = ['#8B8B8B', '#D4A843', '#8B7355'];
const targetIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
const pinIcon = new L.Icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const FitBounds: React.FC<{ pts: [number, number][] }> = ({ pts }) => {
  const map = useMap();
  useEffect(() => {
    if (pts.length && map.getPane('mapPane')) {
      setTimeout(
        () => map.fitBounds(L.latLngBounds(pts), { padding: [50, 50] }),
        0
      );
    }
  }, [pts, map]);
  return null;
};

interface Props {
  onPlayAgain: () => void;
  leaderBoard: LeaderboardEntry[];
  gameInfo: any;
}

const GeoGameOver: React.FC<Props> = ({
  onPlayAgain,
  leaderBoard,
  gameInfo,
}) => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMapReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const [top3Summary, setTop3Summary] =
    useState<TopPlayerSummary>(mockTopPlayerSummary);
  const hookLeaderboard = useLeaderboard(gameInfo.gameId);
  const leaderboardEntry =
    leaderBoard.length > 0 ? leaderBoard : hookLeaderboard;
  const [currentResult, setCurrentResult] = useState<GeoResult>(mockGeoResult);
  const top3 = leaderboardEntry.slice(0, 3) ?? [];
  const podiumOrder =
    top3.length >= 3
      ? [top3[1], top3[0], top3[2]]
      : top3.length === 2
        ? [top3[1], top3[0]]
        : [top3[0]].filter(Boolean);
  const allPts: [number, number][] = [
    ...(currentResult
      ? [
          [
            currentResult.targetLocation.latitude,
            currentResult.targetLocation.longitude,
          ] as [number, number],
        ]
      : []),
    ...(currentResult?.pinnedGeoLocations?.map(
      (p) =>
        [p.guessedLocation.latitude, p.guessedLocation.longitude] as [
          number,
          number,
        ]
    ) ?? []),
  ];
  //fetch group of Top 3 finalist
  useEffect(() => {
    fetchTopThreeGeoFinalistStream(gameInfo.gameId)
      .then((res) => {
        setTop3Summary(res.data);
      })
      .catch((err) => {});
  }, []);
  // get final result
  useEffect(() => {
    fetchCurrentGeoRoundResult(gameInfo.gameId)
      .then((res) => {
        setCurrentResult(res.data);
      })
      .catch((err) => {});
  }, []);

  //call leadership board api if no leader board is present
  // Now handled by useLeaderboard hook

  return (
    <div className="ggo">
      <Confetti active />
      <div className="ggo__left">
        <button
          className="ggo__back"
          onClick={() => navigate(`/dashboard/games/game/${gameInfo.gameId}`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </button>

        {/* Map */}
        <div className="ggo__map">
          <div className="ggo__map-overlay">
            <p className="ggo__map-sub">
              • {gameInfo.gameTitle} • {leaderboardEntry.length} players
            </p>
          </div>
          {mapReady && (
            <MapContainer
              center={
                [
                  currentResult?.targetLocation.latitude,
                  currentResult?.targetLocation.longitude,
                ] as [number, number]
              }
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
              <FitBounds pts={allPts} />
              <Marker
                position={
                  [
                    currentResult?.targetLocation.latitude,
                    currentResult?.targetLocation.longitude,
                  ] as [number, number]
                }
                icon={targetIcon}
              >
                <Popup>{currentResult?.targetLocation.cityName}</Popup>
              </Marker>
              {currentResult?.pinnedGeoLocations.map((p) => (
                <Marker
                  key={p.playerName}
                  position={
                    [
                      p.guessedLocation.latitude,
                      p.guessedLocation.longitude,
                    ] as [number, number]
                  }
                  icon={pinIcon}
                >
                  <Popup>{p.playerName}</Popup>
                </Marker>
              ))}
              {currentResult?.pinnedGeoLocations.map((p) => (
                <Polyline
                  key={`l-${p.playerName}`}
                  positions={
                    [
                      [p.guessedLocation.latitude, p.guessedLocation.longitude],
                      [
                        currentResult?.targetLocation.latitude,
                        currentResult?.targetLocation.longitude,
                      ],
                    ] as [number, number][]
                  }
                  pathOptions={{
                    color: randomColor(),
                    weight: 2,
                    dashArray: '5,10',
                  }}
                />
              ))}
            </MapContainer>
          )}
        </div>

        {/* Round breakdown */}
        <div className="ggo__rounds">
          <h3 className="ggo__rounds-title">Round-By-Round Breakdown</h3>
          {top3Summary.targetLocations.map((target, index) => (
            <div key={index} className="ggo__round-card">
              <div className="ggo__round-header">
                <div>
                  <h4 className="ggo__round-name">
                    Round {index + 1} — {target.cityName}
                  </h4>
                </div>
                {/* <div className="ggo__round-score">
                  <span className="ggo__round-score-lbl">Closest Distance</span>
                </div> */}
              </div>
              <div className="ggo__round-players">
                {top3Summary.top3Players[index + 1] &&
                  top3Summary.top3Players[index + 1].map((p, i) => (
                    <div key={i} className="ggo__round-player">
                      <span
                        className="ggo__round-dot"
                        style={{ background: randomColor() }}
                      >
                        {p.playerName.slice(0, 2)}
                      </span>
                      <span className="ggo__round-pname">{p.playerName}</span>
                      <span className="ggo__round-dist">
                        {Math.round(p.distance)}km
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right sidebar */}
      <div className="ggo__right">
        <h2 className="ggo__title">Final Results</h2>
        <p className="ggo__sub">{gameInfo.gameTitle}</p>

        <div className="ggo__stats">
          <div className="ggo__stat">
            <span className="ggo__stat-val">{leaderBoard.length}</span>
            <span className="ggo__stat-lbl">Players</span>
          </div>
          <div className="ggo__stat">
            <span className="ggo__stat-val">
              {(() => {
                const all = Object.values(top3Summary.top3Players).flat();
                if (!all.length) return '—';
                return Math.round(Math.min(...all.map((p) => p.distance)));
              })()}{' '}
              km
            </span>
            <span className="ggo__stat-lbl">Shortest Guess</span>
          </div>
          <div className="ggo__stat">
            <span className="ggo__stat-val">
              {(() => {
                if (!leaderboardEntry.length) return '—';
                const shortest = Math.min(
                  ...leaderboardEntry.map((p) => p.totalTime)
                );
                if (shortest >= 60)
                  return `${Math.floor(shortest / 60)}m ${shortest % 60}s`;
                return `${shortest}s`;
              })()}
            </span>
            <span className="ggo__stat-lbl">Shortest Duration</span>
          </div>
        </div>

        {/* Podium */}
        <div className="ggo__podium">
          {podiumOrder &&
            podiumOrder.map((p, i) => {
              const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
              return (
                <div key={p.playerName} className="ggo__podium-col">
                  <span
                    className="ggo__podium-avatar"
                    style={{
                      background: p.iconColor,
                      borderColor:
                        place === 1 ? 'var(--color-primary)' : p.iconColor,
                    }}
                  >
                    {p.playerIcon}
                  </span>
                  <span className="ggo__podium-name">{p.playerName}</span>
                  <div
                    className={`ggo__podium-block ggo__podium-block--${place}`}
                    style={{ background: podiumColors[i] }}
                  >
                    <span className="ggo__podium-place">{place}</span>
                    <span className="ggo__podium-score">{p.totalPoints}</span>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Location info */}
        <div className="ggo__loc-card">
          <div className="ggo__loc-row">
            <span className="ggo__loc-pin">📍</span>
            <div>
              <h4>{currentResult?.targetLocation.cityName}</h4>

              <p>
                {currentResult?.targetLocation.latitude.toFixed(4)}° N,{' '}
                {currentResult?.targetLocation.longitude.toFixed(4)}° E
              </p>
            </div>
          </div>
        </div>

        {/* Final Rankings */}
        <h4 className="ggo__rankings-title">Final Rankings</h4>
        <div className="ggo__rankings">
          {leaderboardEntry.map((p) => (
            <div
              key={p.playerName}
              className="ggo__rank-row"
              style={{
                borderColor:
                  p.rank <= 3
                    ? 'var(--color-primary)'
                    : 'rgba(255,255,255,0.08)',
              }}
            >
              <span className="ggo__rank-num">#{p.rank}</span>
              <span
                className="ggo__rank-avatar"
                style={{ background: p.iconColor }}
              >
                {p.playerIcon}
              </span>
              <div className="ggo__rank-info">
                <span className="ggo__rank-name">{p.playerName}</span>
                {/* <span className="ggo__rank-delta">
                  +{p.scoreDelta.toLocaleString()}
                </span> */}
              </div>
              <span className="ggo__rank-score">{p.totalPoints}</span>
            </div>
          ))}
        </div>

        <button className="ggo__btn ggo__btn--primary" onClick={onPlayAgain}>
          🏆 Play Again — Same Settings
        </button>
      </div>
    </div>
  );
};

export default GeoGameOver;
